/**
 * Виджет «2D-чертёж» — главная рабочая область редактора.
 *
 * Сам по себе он ничего не рисует: держит вьюпорт (зум/панораму), читает слои из
 * стора проекта и собирает в одном SVG компоненты-слои снизу вверх:
 * комнаты → стены → проёмы → мебель → подписи → объекты на стенах → трубы → проводка →
 * светильники на потолке → размеры → комментарии → углы → черновики. Как выглядит стена, розетка или кабель, знает
 * только соответствующий слой и его компоненты. Выключенный слой не рисуется.
 *
 * Здесь же — маршрутизация событий между фичами: клик по стене может значить
 * «поставить дверь», «поставить розетку», «начать стену» или «выделить» —
 * в зависимости от инструмента.
 *
 * Управление:
 *   колесо — зум к курсору; панорама — перетаскивание пустого места инструментом выделения,
 *   средняя или правая кнопка, Shift+ЛКМ;
 *   при черчении: цифры — точная длина, Alt — свободный режим без привязок.
 *   Тач: один палец — как мышь, два пальца — панорама и зум щипком, черновики при этом отменяются.
 */
import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import {
	buildWallOutlines,
	cableRoute,
	detectRooms,
	freestandingRoomNodes,
	isOverviewScale,
	lodProps,
	roomLabelName,
	wallSideAt,
} from "@/widgets/plan";
import { CatalogSectionKind } from "@/shared/plan";
import { DrawerSection, SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { planStageAllows, useProjectStore } from "@/shared/store";
import {
	DrawHint,
	LengthInput,
	RoomPreview,
	WallDraft,
	useDrawWall,
	usePlaceRoom,
	RoomDraft,
	DrawClearances2D,
	wallDrawClearances,
	wallDraftOutlines,
} from "@/widgets/draw-wall";
import { useTransformPlan, usePlaceOpening } from "@/shared/hooks";
import { usePlaceFixture, wallFixtureSpotInRoom } from "@/widgets/place-fixture";
import { useEditorHotkeys } from "@/widgets/editor-hotkeys";
import { useDrawCable, useDrawLightingConnection } from "@/widgets/electrical";
import { useDrawPipe } from "@/widgets/plumbing";
import { CableDraft, PipeDraft } from "@/widgets/catalog";
import { useTouchGestures, useViewport } from "@/shared/hooks";
import { EDITOR_PANELS, editorInsets } from "@/shared/config";
import {
	RoomsLayer2D,
	BaseboardsLayer2D,
	RoomLabels2D,
	RoomNameDialog,
	WallsLayer2D,
	OpeningsLayer2D,
	OpeningGhost2D,
	OpeningClearances2D,
	FloorItemsLayer2D,
	WallItemsLayer2D,
	CablesLayer2D,
	LightingConnections2D,
	PipesLayer2D,
	CeilingLightsLayer2D,
	DimensionsLayer2D,
	NodesLayer2D,
	CommentsLayer2D,
	Clearances2D,
	type ClearanceTarget,
	PlacementGhost2D,
	UnderlayLayer2D,
} from "@/widgets/layers";
import {
	UnderlayRecognizedWalls2D,
	UnderlayScaleDialog,
	UnderlayScaleLine,
	underlayBounds,
	useUnderlayTools,
} from "@/widgets/underlay";
import {
	CommentEditor,
	CommentMenu,
	COMMENT_STACK_PX,
	NEW_COMMENT_OFFSET_PX,
	commentAnchor,
	commentTargetAt,
	defaultCommentTarget,
	isCommentVisible,
} from "@/widgets/comments";
import {
	effectiveWallGraph,
	findFixture,
	initialWallGraph,
	type Fixture,
	type FloorItem,
	type PlanLayers,
	type Wall,
	visibleCeilingItems,
	visibleFixtures,
	visibleFloorItems,
} from "@/shared/plan";
import { useCommentsStore, useWallsStore } from "@/shared/store/layers";
import { CanvasGrid } from "./CanvasGrid";
import { HoverTints } from "./HoverTints";
import { useEraseTool } from "../hooks/useEraseTool";
import { useCommentAttach } from "../hooks/useCommentAttach";
import { useCanvasNavigation } from "../hooks/useCanvasNavigation";
import { useKeyboardPan } from "../hooks/useKeyboardPan";
import { commentTargetUnder } from "../logic";
import { SnapMarker } from "./SnapMarker";
import { AlignmentGuides } from "./AlignmentGuides";
import { PlanStageSwitcher } from "./PlanStageSwitcher";

const clearanceTargetOf = (layers: PlanLayers, id: string): ClearanceTarget | null => {
	const item =
		layers.furniture.items[id] ?? layers.plumbing.items[id] ?? layers.electrical.items[id];
	if (item) return { kind: "item", item };
	const fixture = findFixture(layers, id)?.fixture;
	return fixture ? { kind: "fixture", fixture } : null;
};

/** Насколько размер вдоль стены отходит от её грани при черчении, px */
const DRAW_CLEARANCE_GAP_PX = 14;

export const PlanEditor2D = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const rawGraph = layers.walls;
	const planStage = useEditorStore((s) => s.planStage);
	const graph =
		planStage === "initial" || planStage === "demolition"
			? initialWallGraph(rawGraph)
			: effectiveWallGraph(rawGraph);
	const tool = useEditorStore((s) => s.tool);
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	// На этапах объектов («Мебель», «Электрика»…) стены и проёмы готовы: их не выделяют и не двигают
	const wallsLocked = !planStageAllows(planStage, "walls");
	const selectionKind = useEditorStore((s) => s.selection?.kind);
	const gridStep = useEditorStore((s) => s.gridStep);
	const setSelection = useEditorStore((s) => s.setSelection);
	const setTool = useEditorStore((s) => s.setTool);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const drawer = useEditorStore((s) => s.drawer);
	const isPlannerLeftPanelWide = useEditorStore((s) => s.isPlannerLeftPanelWide);
	const showLightingConnections = useEditorStore((s) => s.showLightingConnections);

	const viewport = useViewport({ x: -2500, y: -2500, w: 13000, h: 9000 });
	const { svgRef, box, toWorld, panByPixels, mmPerPx, zoomAt } = viewport;
	const touch = useTouchGestures({ zoomAt, panByPixels });
	useKeyboardPan({ panByPixels });

	const draw = useDrawWall({ toWorld, mmPerPx });
	const room = usePlaceRoom({ toWorld });
	const underlay = useUnderlayTools({ toWorld });
	// Колесо, щипок, два пальца и пробел — двигают и зумят вид в любой момент, не обрывая черчение
	const navigation = useCanvasNavigation({ svgRef, zoomAt, panByPixels });
	const cable = useDrawCable({ toWorld });
	const lightingConnection = useDrawLightingConnection();
	const pipe = useDrawPipe({ toWorld });
	const transform = useTransformPlan({
		toWorld,
		panByPixels,
		mmPerPx,
		// Предмет на стене ездит по стенам своей комнаты и не уходит сквозь стену в соседнюю
		wallSpot: wallFixtureSpotInRoom,
	});
	const placing = usePlaceOpening();
	/** Проём, который сейчас тянут вдоль стены, — у него видны размеры до углов */
	const [movingOpeningId, setMovingOpeningId] = useState<string | null>(null);
	const fixtures = usePlaceFixture();
	/**
	 * Мебель и светильники пропускают клик сквозь себя: ставят предмет из каталога, назначают
	 * комнате покрытие или чертят стену — клик должен дойти до холста или стены под ними
	 */
	const objectsClickThrough = fixtures.placing || fixtures.assigningRoom || draw.active;
	const erase = useEraseTool();
	const commentAttach = useCommentAttach(tool === Tool.Comment);

	// Размеры от предмета до стен — пока зажат Ctrl/Cmd: у предмета под курсором,
	// а если курсор не на предмете — у выделенного
	const [measuring, setMeasuring] = useState(false);
	/** Предмет под курсором — на полу или на стене: его зона захвата «item» или «fixture» */
	const [hoverObjectId, setHoverObjectId] = useState<string | null>(null);
	useEffect(() => {
		const sync = (e: KeyboardEvent) => setMeasuring(e.ctrlKey || e.metaKey);
		const reset = () => setMeasuring(false);
		window.addEventListener("keydown", sync);
		window.addEventListener("keyup", sync);
		window.addEventListener("blur", reset);
		return () => {
			window.removeEventListener("keydown", sync);
			window.removeEventListener("keyup", sync);
			window.removeEventListener("blur", reset);
		};
	}, []);
	const selectedObjectId = useEditorStore((s) =>
		s.selection?.kind === SelectionKind.Item ||
		s.selection?.kind === SelectionKind.Fixture ||
		s.selection?.kind === SelectionKind.Light
			? s.selection.id
			: null,
	);
	const cancelDrafts = () => {
		draw.cancel();
		cable.cancel();
		lightingConnection.cancel();
		pipe.cancel();
	};
	// Правый или двойной клик заканчивает черчение: последняя стена выделена, перебор длины
	// правят справа, не выходя из инструмента
	const finishDrafts = () => {
		draw.finish();
		cable.cancel();
		lightingConnection.cancel();
		pipe.cancel();
	};
	useEditorHotkeys({ onCancelDraft: cancelDrafts, onUndoDraft: draw.stepBack });

	const rooms = detectRooms(graph);
	const outlines = buildWallOutlines(graph);
	const demolishedGhostOutlines = ((): Record<string, (typeof outlines)[string]> | undefined => {
		if (planStage !== "construction") return undefined;
		const initialOutlines = buildWallOutlines(initialWallGraph(rawGraph));
		const ghosts: Record<string, (typeof outlines)[string]> = {};
		for (const wall of Object.values(rawGraph.walls)) {
			if (wall.demolished && initialOutlines[wall.id]) {
				ghosts[wall.id] = initialOutlines[wall.id];
			}
		}
		return Object.keys(ghosts).length > 0 ? ghosts : undefined;
	})();
	const scale = mmPerPx();
	// Черновик стены стыкуется в углу, как встанет после клика: соседние стены в этом углу
	// рисуются уже с новым стыком. Недопустимый черновик соседей не трогает
	const draftOutlines =
		draw.active && !draw.room && !draw.ghost && draw.from && draw.preview
			? wallDraftOutlines(
					graph,
					draw.from,
					draw.preview.point,
					draw.thickness,
					draw.preview.stretch,
				)
			: null;
	const wallOutlines =
		draftOutlines && !draw.invalid ? { ...outlines, ...draftOutlines.neighbors } : outlines;
	// Выключенный слой стен — пустой холст: рисовать по нему всё равно можно
	const walls = graph.visible ? Object.values(graph.walls) : [];
	const nodes = Object.values(graph.nodes);
	// Настенные объекты знают координаты через граф даже при скрытых стенах:
	// это нужно для самостоятельных листов электрики и сантехники в PDF.
	const points = visibleFixtures(layers);
	const floorItems = visibleFloorItems(layers);
	const ceilingItems = visibleCeilingItems(layers);
	const cables = layers.wiring.visible ? Object.values(layers.wiring.items) : [];
	const pipes = layers.pipes.visible ? Object.values(layers.pipes.items) : [];
	// Комментарий к объекту виден вместе со слоем объекта и указывает туда, где объект сейчас;
	// у того, чью стрелку тянут, — туда, куда её дотянули
	const comments = Object.values(layers.comments.items)
		.filter((comment) => isCommentVisible(comment, layers))
		.map((comment) =>
			comment.id === commentAttach.draggingId
				? comment
				: { ...comment, anchor: commentAnchor(comment, layers) },
		);
	// Комментарий, текст которого правят в окошке, и комментарий с открытым меню
	const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
	const [menuCommentId, setMenuCommentId] = useState<string | null>(null);
	/** Комната, которую переименовывают кликом по её подписи */
	const [renamingRoomId, setRenamingRoomId] = useState<string | null>(null);
	const editingComment = editingCommentId ? layers.comments.items[editingCommentId] : undefined;
	const menuComment = menuCommentId ? layers.comments.items[menuCommentId] : undefined;

	// Панель свойств попросила комментарий к выделенному объекту: выноска рядом с объектом,
	// стрелка на него, сразу окошко для текста — как при клике инструментом «Коммент.»
	const commentFor = useEditorStore((s) => s.commentFor);
	useEffect(() => {
		if (!commentFor) return;
		useEditorStore.getState().setCommentFor(null);
		const placed = defaultCommentTarget(
			useProjectStore.getState().project.layers,
			commentFor.kind,
			commentFor.id,
		);
		if (!placed) return;
		const px = mmPerPx();
		// Комментарии одного объекта — лесенкой, а не друг на друга
		const earlier = Object.values(useCommentsStore.getState().items).filter(
			(comment) => comment.target?.kind === commentFor.kind && comment.target.id === commentFor.id,
		).length;
		const frameCenter = {
			x: placed.anchor.x + NEW_COMMENT_OFFSET_PX.x * px,
			y: placed.anchor.y + (NEW_COMMENT_OFFSET_PX.y + earlier * COMMENT_STACK_PX) * px,
		};
		useProjectStore.getState().pushHistory();
		const id = useCommentsStore.getState().addComment(placed.anchor, frameCenter, placed.target);
		useEditorStore.getState().setSelection({ kind: SelectionKind.Comment, id });
		useEditorStore.getState().setTool(Tool.Select);
		setEditingCommentId(id);
		// Запрос разовый: остальное читаем из сторов в момент запроса
		// oxlint-disable-next-line react/exhaustive-deps
	}, [commentFor]);

	/** Правку закончили: пустой комментарий не оставляем — кликнули и передумали */
	const finishCommentEditing = () => {
		const id = editingCommentId;
		setEditingCommentId(null);
		if (!id) return;
		const comment = useCommentsStore.getState().items[id];
		if (comment && comment.text.trim() === "") {
			useCommentsStore.getState().removeComment(id);
			setSelection(null);
		}
	};

	// Вписываем чертёж в видимую часть холста при открытии и каждый раз, когда чертёж
	// заменили целиком (открыли файл, включили демо): иначе новый план остался бы в старом окне.
	// При этом запоминаем `planRevision` сразу, даже если план пустой, чтобы постройка первой стены
	// не вызывала резкий автозум на неё.
	const planRevision = useProjectStore((s) => s.planRevision);
	const fittedRevision = useRef<number | null>(null);
	useEffect(() => {
		if (fittedRevision.current === planRevision) return;
		fittedRevision.current = planRevision;
		if (nodes.length === 0) return;
		viewport.fit(
			{
				minX: Math.min(...nodes.map((n) => n.x)),
				minY: Math.min(...nodes.map((n) => n.y)),
				maxX: Math.max(...nodes.map((n) => n.x)),
				maxY: Math.max(...nodes.map((n) => n.y)),
			},
			editorInsets(
				drawer !== null,
				drawer === DrawerSection.Fixtures ? EDITOR_PANELS.catalogDrawer : undefined,
				isPlannerLeftPanelWide ? EDITOR_PANELS.toolRailWide : undefined,
			),
		);
	}, [nodes, viewport, drawer, planRevision, isPlannerLeftPanelWide]);

	// Загрузили новую подложку — вид наезжает на неё: масштаб задают, глядя на весь план.
	// Подложку, открытую вместе с проектом, не трогаем — там вписывается сам чертёж
	const underlaySrc = layers.underlay.image?.src ?? null;
	const fittedUnderlay = useRef(underlaySrc);
	useEffect(() => {
		if (fittedUnderlay.current === underlaySrc) return;
		fittedUnderlay.current = underlaySrc;
		const bounds = underlayBounds(useProjectStore.getState().project.layers.underlay);
		if (!bounds) return;
		viewport.fit(
			bounds,
			editorInsets(
				drawer !== null,
				drawer === DrawerSection.Fixtures ? EDITOR_PANELS.catalogDrawer : undefined,
				isPlannerLeftPanelWide ? EDITOR_PANELS.toolRailWide : undefined,
			),
		);
	}, [underlaySrc, viewport, drawer, isPlannerLeftPanelWide]);

	// ---- маршрутизация событий -------------------------------------------------

	/**
	 * Инструмент комментария ставит выноску кликом в любое место. Клик по предмету, прибору,
	 * светильнику, проёму или стене сразу привязывает комментарий к нему (стрелка — в точку
	 * клика на объекте), по пустому месту — свободный. Клик ловим на погружении, раньше
	 * объектов, которые иначе забрали бы его себе.
	 * Поставили — комментарий выделен, инструмент снова «Выбор», и сразу открыто окошко
	 * для текста, как в Ремпланнере.
	 */
	const onPointerDownCapture = (e: PointerEvent<SVGSVGElement>) => {
		// Зажат пробел — перетаскивание двигает вид любым инструментом, точка не ставится
		if (navigation.spaceHeld && e.button === 0) {
			e.stopPropagation();
			transform.beginPan(e);
			return;
		}
		if (isReadOnly) {
			// Обработчики слоёв получают pointerdown раньше SVG: в просмотре любой жест
			// по карте — панорамирование, а не сдвиг стены, предмета или его ручки.
			if (touch.handlePointerDown(e)) {
				cancelDrafts();
				transform.handlePointerUp(e);
			} else {
				transform.beginPan(e);
			}
			e.stopPropagation();
			return;
		}
		const roomFloor = (e.target as Element).closest?.('[data-role="room-floor"]');
		const roomId = roomFloor?.getAttribute("data-room-id") ?? null;
		if (
			e.button === 0 &&
			tool === Tool.Select &&
			roomId !== null &&
			useEditorStore.getState().movingFloorPatternRoomId === roomId
		) {
			const floor = layers.floors.items[roomId];
			if (floor) {
				e.stopPropagation();
				transform.beginFloorPatternDrag(e, roomId, floor);
				return;
			}
		}
		// Масштаб и сдвиг подложки: клик их, даже если под курсором стена или мебель
		if (underlay.handlePointerDownCapture(e)) return;
		// Ластик удаляет объект под курсором раньше, чем объект заберёт клик себе
		if (erase.handlePointerDown(e)) return;
		// Комната ставится кликом куда угодно — и поверх мебели, и поверх стены
		if (room.handlePointerDown(e)) return;
		// Точку трассы кабеля или трубы ставят и над мебелью, проёмом, шкафом на стене
		if (cable.handlePointerDownCapture(e) || pipe.handlePointerDownCapture(e)) return;
		if (
			tool !== Tool.Comment ||
			e.button !== 0 ||
			e.shiftKey ||
			(e.pointerType === "touch" && !e.isPrimary)
		)
			return;
		e.stopPropagation();
		e.preventDefault();
		const anchor = toWorld(e.clientX, e.clientY);
		const frameCenter = {
			x: anchor.x + NEW_COMMENT_OFFSET_PX.x * scale,
			y: anchor.y + NEW_COMMENT_OFFSET_PX.y * scale,
		};
		// Кликнули по предмету, прибору, проёму или стене — комментарий сразу привязан к нему
		const under = commentTargetUnder(e.clientX, e.clientY);
		const target = under ? commentTargetAt(layers, under.kind, under.id, anchor) : null;
		commentAttach.clearHover();
		pushHistory();
		const id = useCommentsStore.getState().addComment(anchor, frameCenter, target ?? undefined);
		setSelection({ kind: SelectionKind.Comment, id });
		setTool(Tool.Select);
		setEditingCommentId(id);
	};

	const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
		// Второй палец — жест: всё начатое одним пальцем отменяем, холст только двигаем и зумим
		if (touch.handlePointerDown(e)) {
			cancelDrafts();
			transform.handlePointerUp(e);
			setMovingOpeningId(null);
			return;
		}
		const role = (e.target as HTMLElement).dataset?.role;
		// Пол комнаты — тоже свободное место: на него ставят мебель и за него тянут вид.
		// Сюда клик по полу доходит, только если комната не забрала его себе
		const missed = role === undefined || role === "room-floor";
		// Потянул пустое место инструментом выделения — панорама, как в любом планировщике;
		// клик без движения при этом просто снимает выделение
		const dragEmpty = e.button === 0 && missed && (tool === Tool.Select || tool === Tool.Erase);
		const wantsPan =
			e.button === 1 || e.button === 2 || (e.button === 0 && e.shiftKey) || dragEmpty;
		if (wantsPan) {
			if (dragEmpty) setSelection(null);
			transform.beginPan(e);
			return;
		}
		if (draw.handlePointerDown(e)) return;
		if (cable.handlePointerDown(e)) return;
		if (pipe.handlePointerDown(e)) return;
		// Клик рядом со стеной инструментом «Дверь» / «Окно» — проём встаёт туда, где призрак
		if (
			missed &&
			e.button === 0 &&
			placing.active &&
			placing.dropAt(toWorld(e.clientX, e.clientY))
		) {
			return;
		}
		// Прибор «на стену» кликнули рядом со стеной — встаёт туда, где его призрак прилип к стене.
		// Далеко от стен ставить некуда: шага истории нет, у курсора подсказка
		if (missed && e.button === 0 && fixtures.onWall && fixtures.ghost?.kind === "wall") {
			const world = toWorld(e.clientX, e.clientY);
			useProjectStore.getState().recordEdit(() => fixtures.dropAt(world, e.altKey));
			return;
		}
		// Клик по свободному месту инструментом установки — предмет встаёт на пол
		if (
			missed &&
			e.button === 0 &&
			fixtures.placeOnFloor(toWorld(e.clientX, e.clientY), e.altKey)
		) {
			return;
		}
		// Клик мимо объектов снимает выделение
		if (missed) setSelection(null);
	};

	const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
		if (touch.handlePointerMove(e)) return;
		if (isReadOnly) {
			transform.handlePointerMove(e);
			return;
		}
		underlay.handlePointerMove(e);
		// Ctrl/Cmd могли зажать, пока окно было не в фокусе, — сверяемся и по мыши
		setMeasuring(e.ctrlKey || e.metaKey);
		setHoverObjectId(
			(e.target as Element)
				.closest?.('[data-role="item"][data-id], [data-role="fixture"][data-id]')
				?.getAttribute("data-id") ?? null,
		);
		// До жеста: конец стрелки комментария двигает жест, а подсветку цели — привязка
		commentAttach.handlePointerMove(e.clientX, e.clientY);
		room.handlePointerMove(e);
		if (transform.handlePointerMove(e)) return;
		erase.handlePointerMove(e);
		draw.handlePointerMove(e);
		cable.handlePointerMove(e);
		pipe.handlePointerMove(e);
		// Пока выбран тип в каталоге, за курсором едет призрак предмета
		if (fixtures.placing) fixtures.trackCursor(toWorld(e.clientX, e.clientY), e.altKey);
		// Дверь или окно: призрак едет по ближайшей стене, рядом размеры до углов
		if (placing.active) placing.trackCursor(toWorld(e.clientX, e.clientY));
	};

	const onPointerUp = (e: PointerEvent<SVGSVGElement>) => {
		if (isReadOnly) {
			touch.handlePointerUp(e);
			transform.handlePointerUp(e);
			return;
		}
		setMovingOpeningId(null);
		// Карточку из каталога отпустили над чертежом — ставим туда, где был призрак
		const editor = useEditorStore.getState();
		// Дверь или окно из «Проёмов» отпустили над чертежом — на ближайшую стену
		if (editor.openingDrag) {
			const dropped = placing.dropAt(toWorld(e.clientX, e.clientY));
			editor.endOpeningDrag(!dropped);
			return;
		}
		if (editor.catalogDrag) {
			const dropped = fixtures.dropAt(toWorld(e.clientX, e.clientY), e.altKey);
			editor.endCatalogDrag(!dropped);
			fixtures.trackCursor(null);
			return;
		}
		touch.handlePointerUp(e);
		underlay.handlePointerUp();
		transform.handlePointerUp(e);
		commentAttach.handlePointerUp(e.clientX, e.clientY);
		draw.handlePointerUp(e);
		erase.handlePointerUp(e);
	};

	const onWallPointerDown = (e: PointerEvent<Element>, wall: Wall) => {
		if (isReadOnly) return;
		if (e.button !== 0 || e.shiftKey) return;
		// На этапе «Демонтаж» клик по стене сразу помечает её под снос (или снимает пометку)
		if (planStage === "demolition" && tool === Tool.Select) {
			e.stopPropagation();
			pushHistory();
			useWallsStore.getState().updateWall(wall.id, { demolished: !wall.demolished });
			setSelection({ kind: SelectionKind.Wall, id: wall.id });
			return;
		}
		const world = toWorld(e.clientX, e.clientY);
		// Проём и объект каталога ставятся кликом по самой стене
		if (placing.placeOnWall(wall.id, world) || fixtures.placeOnPlan(wall.id, world)) {
			e.stopPropagation();
			return;
		}
		// Инструментом стены по стене можно чертить: событие уходит на холст, и цепочка
		// начинается прямо от неё — стена сама разрежется в этой точке
		if (tool === Tool.Wall || tool === Tool.Cable || tool === Tool.Pipe) return;
		if (wallsLocked) {
			// Стена на этом этапе — как пустое место: снимает выделение и двигает вид
			e.stopPropagation();
			setSelection(null);
			transform.beginPan(e);
			return;
		}
		e.stopPropagation();
		// Сторона — та половина толщины, по которой кликнули: ей панель предложит отделку
		const a = graph.nodes[wall.a];
		const b = graph.nodes[wall.b];
		const side = a && b ? wallSideAt(a, b, world) : undefined;
		setSelection({ kind: SelectionKind.Wall, id: wall.id, side });
		if (tool === Tool.Select) transform.beginWallDrag(e, wall.id);
	};

	const onFixturePointerDown = (e: PointerEvent<Element>, fixture: Fixture) => {
		if (isReadOnly) return;
		if (e.button !== 0 || e.shiftKey) return;
		// Инструментом кабеля клик по прибору начинает или подключает кабель, трубы — то же с точкой воды
		if (cable.handleFixturePointerDown(e, fixture.id)) return;
		if (lightingConnection.handleFixturePointerDown(e, fixture.id)) return;
		if (pipe.handleFixturePointerDown(e, fixture.id)) return;
		e.stopPropagation();
		setSelection({ kind: SelectionKind.Fixture, id: fixture.id });
		if (tool === Tool.Select) transform.beginFixtureDrag(e, fixture.id);
	};

	const onLightPointerDown = (e: PointerEvent<Element>, item: FloorItem) => {
		if (isReadOnly) return;
		if (e.button !== 0 || e.shiftKey) return;
		// Инструментом кабеля клик по светильнику начинает или подключает кабель
		if (cable.handleFixturePointerDown(e, item.id)) return;
		if (lightingConnection.handleFixturePointerDown(e, item.id)) return;
		// Инструментом установки клик по светильнику ставит новый рядом, при черчении стены —
		// ставит точку стены: не перехватываем
		if (fixtures.placing || draw.active) return;
		e.stopPropagation();
		setSelection({ kind: SelectionKind.Light, id: item.id });
		if (tool === Tool.Select) transform.beginItemDrag(e, item.id);
	};

	// Обзорный масштаб: значки на стенах и потолке, кабели, трубы и узлы стен прячем —
	// при отдалении они налезают друг на друга. Слой остаётся, если он сейчас в работе:
	// включён его инструмент или в нём что-то выделено
	const overview = isOverviewScale(scale);
	const keepWallItems =
		tool === Tool.Place ||
		tool === Tool.Cable ||
		tool === Tool.Pipe ||
		selectionKind === SelectionKind.Fixture;
	const keepLights =
		tool === Tool.Place || tool === Tool.Cable || selectionKind === SelectionKind.Light;
	const keepCables = tool === Tool.Cable || selectionKind === SelectionKind.Cable;
	const keepPipes = tool === Tool.Pipe || selectionKind === SelectionKind.Pipe;
	const keepNodes = tool === Tool.Wall || selectionKind === SelectionKind.Node;

	// При установке из каталога сам предмет едет за курсором призраком — курсору достаточно перекрестия
	const cursor = navigation.spaceHeld
		? "grab"
		: tool === Tool.Select
			? "default"
			: tool === Tool.UnderlayMove
				? "move"
				: "crosshair";
	const activeGuides = draw.active && draw.preview ? draw.preview.guides : transform.guides;
	const stageHint =
		planStage === "demolition"
			? "Кликните по стене, чтобы пометить её под демонтаж или отменить снос"
			: planStage === "construction" && !draw.active
				? "Начертите возводимые перегородки (снесённые стены показаны красным пунктиром)"
				: null;
	const hint = draw.active
		? draw.hint
		: (room.hint ??
			cable.hint ??
			lightingConnection.hint ??
			pipe.hint ??
			(tool === Tool.Place ? fixtures.hint : null) ??
			stageHint);

	return (
		<div className="absolute inset-0">
			<PlanStageSwitcher />
			<svg
				ref={svgRef}
				data-plan-export
				className={`absolute inset-0 block size-full touch-none select-none${
					tool === Tool.Erase ? " plan-erasing" : ""
				}`}
				style={{ cursor }}
				viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
				onPointerDownCapture={onPointerDownCapture}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerUp}
				onPointerLeave={() => {
					fixtures.trackCursor(null);
					placing.trackCursor(null);
					room.handlePointerMove(null);
					erase.clearHover();
					commentAttach.clearHover();
				}}
				onContextMenu={(e) => {
					e.preventDefault();
					finishDrafts();
				}}
				onDoubleClick={finishDrafts}
			>
				<HoverTints erasing={tool === Tool.Erase} />
				<g data-plan-export-grid>
					<CanvasGrid box={viewport.box} step={gridStep} mmPerPx={scale} />
				</g>
				{/* Подложка — фото или скан плана — в самом низу, под комнатами и стенами */}
				<UnderlayLayer2D underlay={layers.underlay} moving={underlay.moving} mmPerPx={scale} />
				<RoomsLayer2D
					rooms={rooms}
					mmPerPx={scale}
					onRoomDragStart={(e, picked, wasSelected) => {
						// Уже выделенную комнату, стоящую отдельно, тащат целиком (общая стена увела
						// бы и соседей); в остальных случаях жест по полу двигает вид, как по пустому месту
						// На этапах объектов комнату не переносят: это правка стен
						const nodeIds =
							wasSelected && !wallsLocked ? freestandingRoomNodes(graph, picked) : null;
						if (nodeIds) transform.beginRoomDrag(e, { nodeIds, inner: picked.inner });
						else transform.beginPan(e);
					}}
				/>
				<WallsLayer2D
					walls={walls}
					outlines={wallOutlines}
					demolishedGhostOutlines={demolishedGhostOutlines}
					mmPerPx={scale}
					onWallPointerDown={onWallPointerDown}
				/>
				<BaseboardsLayer2D
					graph={graph}
					rooms={rooms}
					baseboards={layers.baseboard}
					mmPerPx={scale}
					onBaseboardPointerDown={(event, selectedRoom) => {
						if (event.button !== 0 || tool !== Tool.Select) return;
						event.stopPropagation();
						useEditorStore.getState().setActiveCatalog(CatalogSectionKind.Baseboard);
						setSelection({ kind: SelectionKind.Room, id: selectedRoom.id });
					}}
				/>
				<OpeningsLayer2D
					graph={graph}
					walls={walls}
					mmPerPx={scale}
					onOpeningPointerDown={(e, opening) => {
						if (e.button !== 0) return;
						e.stopPropagation();
						if (wallsLocked) {
							setSelection(null);
							transform.beginPan(e);
							return;
						}
						setSelection({ kind: SelectionKind.Opening, id: opening.id });
						transform.beginOpeningDrag(e, opening.id);
						setMovingOpeningId(opening.id);
					}}
				/>
				<FloorItemsLayer2D
					items={floorItems}
					mmPerPx={scale}
					// Назначение комнате и черчение стен тоже «сквозь» мебель: иначе клик по ковру
					// или кровати выделит их, а пол комнате не назначится и стена не поставится
					placingActive={objectsClickThrough}
					onItemPointerDown={(e, item) => {
						if (e.button !== 0 || e.shiftKey) return;
						// Инструментом установки клик по предмету ставит новый рядом — не перехватываем
						if (objectsClickThrough) return;
						e.stopPropagation();
						setSelection({ kind: SelectionKind.Item, id: item.id });
						if (tool === Tool.Select) transform.beginItemDrag(e, item.id);
					}}
					onRotatePointerDown={(e, item) => {
						if (e.button !== 0) return;
						e.stopPropagation();
						transform.beginItemRotate(e, item.id);
					}}
					onResizePointerDown={(e, item, axis, anchor) => {
						if (e.button !== 0) return;
						e.stopPropagation();
						transform.beginItemResize(e, item.id, axis, anchor);
					}}
					sizeHint={transform.sizeHint}
				/>
				<g {...lodProps(overview && !keepWallItems)}>
					<WallItemsLayer2D
						graph={graph}
						fixtures={points}
						mmPerPx={scale}
						onFixturePointerDown={onFixturePointerDown}
					/>
				</g>
				<g {...lodProps(overview && !keepPipes)}>
					<PipesLayer2D
						graph={graph}
						plumbing={layers.plumbing}
						pipes={pipes}
						mmPerPx={scale}
						onPipePointerDown={(e, item) => {
							if (e.button !== 0 || e.shiftKey) return;
							e.stopPropagation();
							setSelection({ kind: SelectionKind.Pipe, id: item.id });
						}}
					/>
				</g>
				<g {...lodProps(overview && !keepCables)}>
					<CablesLayer2D
						graph={graph}
						electrical={layers.electrical}
						cables={cables}
						mmPerPx={scale}
						onCablePointerDown={(e, item) => {
							if (isReadOnly || e.button !== 0 || e.shiftKey) return;
							e.stopPropagation();
							setSelection({ kind: SelectionKind.Cable, id: item.id });
							const route = cableRoute(graph, layers.electrical, item);
							if (tool === Tool.Select && route) transform.beginCableDrag(e, item.id, route);
						}}
					/>
				</g>
				{showLightingConnections && layers.electrical.visible && layers.wiring.visible && (
					<LightingConnections2D
						graph={graph}
						electrical={layers.electrical}
						connections={Object.values(layers.wiring.connections)}
						mmPerPx={scale}
						onConnectionPointerDown={(event, connection) => {
							if (event.button !== 0 || event.shiftKey) return;
							event.stopPropagation();
							setSelection({ kind: SelectionKind.LightingConnection, id: connection.id });
						}}
					/>
				)}
				<g {...lodProps(overview && !keepLights)}>
					<CeilingLightsLayer2D
						graph={graph}
						electrical={layers.electrical}
						items={ceilingItems}
						mmPerPx={scale}
						placingActive={objectsClickThrough}
						onLightPointerDown={onLightPointerDown}
					/>
				</g>
				{/* Ctrl/Cmd — размеры от выделенного предмета: до стен, а если навели на другой
				    предмет — до него */}
				{(() => {
					if (!measuring || !selectedObjectId) return null;
					const target = clearanceTargetOf(layers, selectedObjectId);
					if (!target) return null;
					const other =
						hoverObjectId && hoverObjectId !== selectedObjectId
							? clearanceTargetOf(layers, hoverObjectId)
							: null;
					return (
						<Clearances2D
							target={target}
							other={other}
							graph={graph}
							rooms={rooms}
							outlines={outlines}
							mmPerPx={scale}
						/>
					);
				})()}
				{/* Призрак предмета из каталога — где он встанет по клику или когда отпустят карточку */}
				<g data-plan-export-exclude>
					<PlacementGhost2D ghost={fixtures.ghost} graph={graph} mmPerPx={scale} />
				</g>
				{/* Дверь или окно ставят или тянут вдоль стены — призрак и размеры до углов стены */}
				{(() => {
					const opening =
						placing.ghost ?? (movingOpeningId ? graph.openings[movingOpeningId] : undefined);
					if (!opening) return null;
					return (
						<g data-plan-export-exclude>
							<OpeningGhost2D ghost={placing.ghost} graph={graph} mmPerPx={scale} />
							<OpeningClearances2D
								graph={graph}
								opening={opening}
								rooms={rooms}
								outlines={outlines}
								mmPerPx={scale}
							/>
						</g>
					);
				})()}
				{/* Подписи комнат — над светильниками и проводкой: значок люстры не закрывает площадь */}
				<RoomLabels2D
					rooms={rooms}
					mmPerPx={scale}
					// Клик по подписи инструментом выделения — переименовать комнату
					onLabelPointerDown={
						!isReadOnly && tool === Tool.Select
							? (e, labelled) => {
									if (e.button !== 0 || e.shiftKey) return;
									e.stopPropagation();
									setRenamingRoomId(labelled.id);
								}
							: undefined
					}
				/>
				<DimensionsLayer2D
					graph={graph}
					wallsVisible={graph.visible}
					rooms={rooms}
					outlines={outlines}
					mmPerPx={scale}
				/>
				<CommentsLayer2D
					comments={comments}
					mmPerPx={scale}
					editingId={editingCommentId}
					onCommentPointerDown={(e, comment, part) => {
						if (e.button !== 0 || e.shiftKey) return;
						e.stopPropagation();
						setSelection({ kind: SelectionKind.Comment, id: comment.id });
						if (tool !== Tool.Select) return;
						// Конец стрелки отрывают от объекта: где отпустят — там и привяжется
						if (part === "anchor") commentAttach.begin(comment.id);
						transform.beginCommentDrag(e, comment.id, part);
					}}
					onCommentDoubleClick={(e, comment) => {
						if (isReadOnly) return;
						// Двойной клик по холсту отменяет черновики — по комментарию он открывает правку
						e.stopPropagation();
						pushHistory();
						setSelection({ kind: SelectionKind.Comment, id: comment.id });
						setEditingCommentId(comment.id);
					}}
					onCommentMenu={(comment) => {
						if (isReadOnly) return;
						setSelection({ kind: SelectionKind.Comment, id: comment.id });
						setMenuCommentId(comment.id);
					}}
				/>
				<g data-plan-export-exclude>
					<AlignmentGuides guides={activeGuides} box={viewport.box} mmPerPx={scale} />
					{/* Узлы — концы стен: на этапах объектов их не двигают, поэтому и не показываем */}
					{graph.visible && !wallsLocked && (
						<g {...lodProps(overview && !keepNodes)}>
							<NodesLayer2D
								nodes={nodes}
								mmPerPx={scale}
								onNodePointerDown={(e, node) => {
									if (e.button !== 0 || e.shiftKey) return;
									e.stopPropagation();
									setSelection({ kind: SelectionKind.Node, id: node.id });
									transform.beginNodeDrag(e, node.id);
								}}
							/>
						</g>
					)}
				</g>

				{/* Черновики: стена, кабель и труба, которые сейчас ведут */}
				{draw.active && draw.room && (
					<RoomDraft
						corners={draw.room.corners}
						width={draw.room.width}
						depth={draw.room.depth}
						thickness={draw.thickness}
						bearing={draw.bearing}
						invalid={draw.invalid}
						mmPerPx={scale}
					/>
				)}
				{draw.active && draw.ghost && <RoomPreview preview={draw.ghost} mmPerPx={scale} />}
				{draw.active && !draw.room && !draw.ghost && draw.from && draw.preview && (
					<WallDraft
						from={draw.from}
						to={draw.preview.point}
						mmPerPx={scale}
						thickness={draw.thickness}
						outline={draftOutlines?.draft}
						bearing={draw.bearing}
						invalid={draw.invalid}
					/>
				)}
				{/* Расстояния от начала и конца стены до соседних стен — пока её ведут или выбирают,
				    откуда начать. У прямоугольников свои размеры на призраке */}
				{draw.active && draw.preview && draw.pointer && !draw.room && !draw.ghost && (
					<DrawClearances2D
						clearances={wallDrawClearances(
							graph,
							outlines,
							draw.from,
							draw.preview.point,
							draw.pointer,
							DRAW_CLEARANCE_GAP_PX * scale,
							draw.preview.kind === "node",
						)}
						mmPerPx={scale}
					/>
				)}
				{draw.active && draw.preview && (
					<SnapMarker snap={draw.preview} mmPerPx={scale} pointer={draw.pointer} />
				)}
				{room.preview && <RoomPreview preview={room.preview} mmPerPx={scale} />}
				{underlay.scaleLine && (
					<UnderlayScaleLine
						from={underlay.scaleLine.from}
						to={underlay.scaleLine.to}
						mmPerPx={scale}
					/>
				)}
				<UnderlayRecognizedWalls2D mmPerPx={scale} />
				{cable.active && cable.route.length > 0 && (
					<CableDraft
						route={cable.route}
						preview={cable.preview}
						canFinish={cable.canFinish}
						color={cable.color}
						mmPerPx={scale}
					/>
				)}
				{pipe.active && pipe.route.length > 0 && (
					<PipeDraft
						route={pipe.route}
						preview={pipe.preview}
						canFinish={pipe.canFinish}
						medium={pipe.medium}
						mmPerPx={scale}
					/>
				)}
			</svg>

			{editingComment && (
				<CommentEditor
					comment={editingComment}
					viewBox={box}
					mmPerPx={scale}
					onChange={(text) =>
						useCommentsStore.getState().updateComment(editingComment.id, { text })
					}
					onFinish={finishCommentEditing}
				/>
			)}
			{menuComment && !editingComment && (
				<CommentMenu
					comment={menuComment}
					viewBox={box}
					mmPerPx={scale}
					onFontSize={(fontSize) => {
						pushHistory();
						useCommentsStore.getState().updateComment(menuComment.id, { fontSize });
					}}
					onColor={(color) => {
						pushHistory();
						useCommentsStore.getState().updateComment(menuComment.id, { color });
					}}
					onEdit={() => {
						pushHistory();
						setMenuCommentId(null);
						setEditingCommentId(menuComment.id);
					}}
					onDelete={() => {
						pushHistory();
						useCommentsStore.getState().removeComment(menuComment.id);
						setMenuCommentId(null);
						setSelection(null);
					}}
					onClose={() => setMenuCommentId(null)}
				/>
			)}
			{renamingRoomId !== null && rooms.some((candidate) => candidate.id === renamingRoomId) && (
				<RoomNameDialog
					roomId={renamingRoomId}
					defaultName={roomLabelName(
						rooms.findIndex((candidate) => candidate.id === renamingRoomId),
					)}
					onClose={() => setRenamingRoomId(null)}
				/>
			)}
			{underlay.measure && (
				<UnderlayScaleDialog
					measure={underlay.measure}
					onApply={underlay.finishScale}
					onClose={underlay.cancelScale}
				/>
			)}
			{hint && (
				<DrawHint
					text={hint}
					tone={draw.active ? draw.hintTone : room.preview?.blocked ? "error" : "info"}
					inset={
						editorInsets(
							drawer !== null,
							drawer === DrawerSection.Fixtures ? EDITOR_PANELS.catalogDrawer : undefined,
							isPlannerLeftPanelWide ? EDITOR_PANELS.toolRailWide : undefined,
						).left
					}
				/>
			)}
			{draw.active && draw.typedLength !== null && (
				<LengthInput
					value={draw.typedLength}
					at={draw.screen}
					angle={draw.angle}
					onChange={draw.setTypedLength}
					onCommit={draw.commitTypedLength}
					onCancel={() => draw.setTypedLength(null)}
					pair={draw.typedPair}
				/>
			)}
		</div>
	);
};
